import { Link } from "react-router-dom";
import { Bot, Gamepad2, ArrowRight, CheckCircle2, Users, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PublicLayout } from "@/components/PublicLayout";

export default function HomePage() {
  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <h1 className="font-nunito font-black text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Workshops Incríveis para{" "}
              <span className="gradient-text">Crianças</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Robótica e Atleta Digital num único evento incrível para desenvolver 
              habilidades do futuro. Faça o agendamento agora!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/verificar">
                <Button 
                  size="lg" 
                  className="rounded-full px-8 btn-animate w-full sm:w-auto"
                  data-testid="schedule-workshop-button"
                >
                  Agendar Workshop
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Workshop Types */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-nunito font-bold text-2xl sm:text-3xl text-center mb-12">
            Atividades do Evento
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Robotics Card */}
            <Card className="workshop-card overflow-hidden border-0 shadow-lg stagger-item" data-testid="robotics-card">
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/7750751/pexels-photo-7750751.jpeg" 
                  alt="Workshop de Robótica"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-violet-900/80 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 text-white mb-2">
                    <Bot className="w-6 h-6" />
                    <span className="font-nunito font-bold text-xl">Robótica</span>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-muted-foreground mb-4">
                  Construa e programe robôs! Aprenda mecânica, eletrônica e programação 
                  de forma divertida e interativa.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-violet-500" />
                    Montagem de robôs
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-violet-500" />
                    Programação básica
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-violet-500" />
                    Desafios em equipe
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Digital Athlete Card */}
            <Card className="workshop-card overflow-hidden border-0 shadow-lg stagger-item" data-testid="digital-athlete-card">
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/4145035/pexels-photo-4145035.jpeg" 
                  alt="Workshop Atleta Digital"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/80 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 text-white mb-2">
                    <Gamepad2 className="w-6 h-6" />
                    <span className="font-nunito font-bold text-xl">Atleta Digital</span>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-muted-foreground mb-4">
                  Entre no mundo dos e-sports! Desenvolva reflexos, estratégia e 
                  trabalho em equipe com games educativos.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Games educativos
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Estratégia e raciocínio
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Competições amigáveis
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-nunito font-bold text-2xl sm:text-3xl text-center mb-12">
            Como Funciona
          </h2>
          <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center stagger-item">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-nunito font-bold text-lg mb-2">1. Verifique o Cadastro</h3>
              <p className="text-sm text-muted-foreground">
                Informe o nome e data de nascimento da criança para verificar se está cadastrada.
              </p>
            </div>
            <div className="text-center stagger-item">
              <div className="w-16 h-16 rounded-2xl bg-violet-500/10 flex items-center justify-center mx-auto mb-4">
                <Bot className="w-8 h-8 text-violet-500" />
              </div>
              <h3 className="font-nunito font-bold text-lg mb-2">2. Escolha o Horário</h3>
              <p className="text-sm text-muted-foreground">
                Selecione a data e o horário desejados para o evento completo.
              </p>
            </div>
            <div className="text-center stagger-item">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="font-nunito font-bold text-lg mb-2">3. Confirme a Presença</h3>
              <p className="text-sm text-muted-foreground">
                Agende o horário desejado e receba a confirmação via WhatsApp.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/verificar">
              <Button 
                size="lg" 
                className="rounded-full px-8 btn-animate"
                data-testid="start-scheduling-button"
              >
                Começar Agendamento
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
